/* ============================================================
   SolarBalls — Custom quick-view controller.
   Opens a branded modal, loads product markup via the Section
   Rendering API (/products/{handle}?section_id=sb-quickview), and
   wires up gallery/thumbnails, read-more and variant switching.
   Quantity stepper + AJAX add-to-cart + cart drawer are handled by
   the theme's own delegated JS (data-quantity-* / data-action-atc).
   ============================================================ */
(function () {
  'use strict';
  var SECTION = 'sb-quickview';
  var overlay, body, lastFocus, currentAbort;

  function buildShell() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'sbqv-overlay';
    overlay.setAttribute('hidden', '');
    overlay.innerHTML =
      '<div class="sbqv-modal" role="dialog" aria-modal="true" aria-label="Quick view">' +
        '<button type="button" class="sbqv-close" data-sbqv-close aria-label="Close quick view">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="sbqv-scroll"><div class="sbqv-body" data-sbqv-body><div class="sbqv-loading" aria-live="polite"><span class="sbqv-spinner"></span></div></div></div>' +
      '</div>';
    document.body.appendChild(overlay);
    body = overlay.querySelector('[data-sbqv-body]');

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.closest('[data-sbqv-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) close();
    });
  }

  function open(url) {
    buildShell();
    lastFocus = document.activeElement;
    overlay.removeAttribute('hidden');
    document.documentElement.classList.add('sbqv-lock');
    // spinner while loading
    body.innerHTML = '<div class="sbqv-loading" aria-live="polite"><span class="sbqv-spinner"></span></div>';
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });

    var fetchUrl = new URL(url, location.origin);
    fetchUrl.search = ''; fetchUrl.hash = '';
    fetchUrl.searchParams.set('section_id', SECTION);

    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();

    fetch(fetchUrl.toString(), { signal: currentAbort.signal, headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        body.innerHTML = html;
        initContent();
        var closeBtn = overlay.querySelector('[data-sbqv-close]');
        if (closeBtn) closeBtn.focus();
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        // graceful fallback — send them to the product page
        window.location.href = url;
      });
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('sbqv-lock');
    var done = function () {
      overlay.setAttribute('hidden', '');
      body.innerHTML = '';
      overlay.removeEventListener('transitionend', done);
    };
    overlay.addEventListener('transitionend', done);
    setTimeout(done, 320); // fallback if no transition
    if (lastFocus && lastFocus.focus) try { lastFocus.focus(); } catch (e) {}
  }

  /* ---------- content wiring ---------- */
  function initContent() {
    var root = body.querySelector('[data-sbqv-root]');
    if (!root) return;
    initGallery(root);
    initReadMore(root);
    initVariants(root);
    initCloseOnAdd(root);
  }

  // After add-to-cart, the theme opens its cart drawer — but our overlay covers
  // it (esp. on mobile). Wait for the drawer to open, then close the quick-view
  // so the cart is visible. Falls back to a timeout if the drawer isn't detected.
  function initCloseOnAdd(root) {
    var atc = root.querySelector('[data-sbqv-atc]');
    if (!atc) return;
    atc.addEventListener('click', function () {
      if (atc.hasAttribute('data-soldout')) return; // don't close on a dead button
      var start = Date.now();
      var poll = setInterval(function () {
        var d = document.querySelector('#t4s-mini_cart, .t4s-mini_cart');
        var open = (d && (d.classList.contains('t4s-drawer--open') || d.classList.contains('is-open'))) ||
          document.documentElement.className.indexOf('mini_cart-opened') > -1 ||
          document.documentElement.classList.contains('t4s-drawer-opened');
        if (open || Date.now() - start > 1800) { clearInterval(poll); close(); }
      }, 120);
    });
  }

  function initGallery(root) {
    var mains = Array.prototype.slice.call(root.querySelectorAll('.sbqv__main'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('.sbqv__thumb'));
    var strip = root.querySelector('[data-sbqv-thumbs]');
    var total = mains.length;
    if (total <= 1) return;
    var cur = 0;
    function show(idx) {
      idx = ((idx % total) + total) % total; // wrap around
      cur = idx;
      mains.forEach(function (m) { m.classList.toggle('is-active', +m.getAttribute('data-idx') === idx); });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', +t.getAttribute('data-idx') === idx); });
      // keep the active thumbnail visible in the scroll strip (no page jump)
      var at = thumbs[idx];
      if (strip && at) strip.scrollLeft = at.offsetLeft - (strip.clientWidth / 2) + (at.clientWidth / 2);
    }
    thumbs.forEach(function (t) { t.addEventListener('click', function () { show(+t.getAttribute('data-idx')); }); });
    var prev = root.querySelector('[data-sbqv-prev]');
    var next = root.querySelector('[data-sbqv-next]');
    if (prev) prev.addEventListener('click', function () { show(cur - 1); });
    if (next) next.addEventListener('click', function () { show(cur + 1); });
    root._sbqvShow = show;
  }

  function initReadMore(root) {
    var wrap = root.querySelector('[data-sbqv-desc]');
    if (!wrap) return;
    var text = wrap.querySelector('[data-sbqv-desc-text]');
    var btn = wrap.querySelector('[data-sbqv-readmore]');
    if (!text || !btn) return;
    // reveal the toggle only when the text actually overflows the clamp
    requestAnimationFrame(function () {
      if (text.scrollHeight - text.clientHeight > 4) btn.hidden = false;
    });
    btn.addEventListener('click', function () {
      var expanded = wrap.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
    });
  }

  function initVariants(root) {
    var dataEl = root.querySelector('[data-sbqv-variants]');
    var atc = root.querySelector('[data-sbqv-atc]');
    var priceBox = root.querySelector('[data-sbqv-price]');
    if (!dataEl || !atc) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var optionWraps = root.querySelectorAll('.sbqv__opt');
    if (!optionWraps.length) return; // single-variant product

    // current selection = active swatch per option group
    var selected = [];
    optionWraps.forEach(function (w, i) {
      var on = w.querySelector('.sbqv__swatch.is-active') || w.querySelector('.sbqv__swatch');
      selected[i] = on ? on.getAttribute('data-value') : null;
    });

    function match() {
      return variants.find(function (v) {
        return v.options.every(function (o, i) { return o === selected[i]; });
      });
    }
    function apply() {
      var v = match();
      if (!v) return;
      atc.setAttribute('data-variant-id', v.id);
      if (priceBox && v.priceHtml) priceBox.innerHTML = v.priceHtml;
      var txtEl = atc.querySelector('[data-sbqv-atc-text]');
      if (v.available) {
        atc.removeAttribute('data-soldout'); atc.removeAttribute('aria-disabled');
        if (txtEl && txtEl.textContent.trim().toLowerCase() === 'sold out') txtEl.textContent = 'Add to cart';
      } else {
        atc.setAttribute('data-soldout', 'true'); atc.setAttribute('aria-disabled', 'true');
        if (txtEl) txtEl.textContent = 'Sold out';
      }
      if (root._sbqvShow && typeof v.imgIdx === 'number') root._sbqvShow(v.imgIdx);
    }

    optionWraps.forEach(function (w, i) {
      w.addEventListener('click', function (e) {
        var sw = e.target.closest('.sbqv__swatch');
        if (!sw) return;
        w.querySelectorAll('.sbqv__swatch').forEach(function (s) { s.classList.remove('is-active'); });
        sw.classList.add('is-active');
        selected[i] = sw.getAttribute('data-value');
        var lbl = w.querySelector('[data-sbqv-opt-selected]');
        if (lbl) lbl.textContent = sw.getAttribute('data-value');
        apply();
      });
    });
  }

  /* ---------- trigger interception ---------- */
  // Intercept the best-sellers quick-view eye (and any element opted-in with
  // data-sb-qv). Capture phase + stopImmediatePropagation blocks the theme's
  // native quick-view so only ours opens.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.sbh-bs__qv, [data-sb-qv]');
    if (!trigger) return;
    var url = trigger.getAttribute('href') || trigger.getAttribute('data-sb-qv-url');
    if (!url || url === '#') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open(url);
  }, true);
})();
